import axios from 'axios'
import modalStyles from '@/features/application/styles/modals.module.css'
import productValidationStyles from '@/features/product/styles/productValidation.module.css'
import { NOTIFICATION_TITLES } from '@/constants/notifications'
import { isGdprConsentRequiredError } from '@/features/document-management/constants/gdprConsent'

/** Reimbursement start blocked past lease end — shown with its own dialog title. */
const REIMBURSEMENT_POLICY_EXPIRED_MESSAGE =
  'You can not start a reimbursement after the Lease has ended. This Policy has Expired.'

/**
 * Feedback UX:
 * - Success / info / warning → compact top-centered toast (non-blocking, auto-dismiss).
 * - Errors & critical `showNotification` → centered modal with backdrop (same shell as ValidationMessageModal:
 *   title row, body, blue Ok — readable for long/HTML errors).
 */

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

interface NotificationOptions {
  /** ms; 0 = stay until dismissed */
  duration?: number
  type?: NotificationType
  /** Modal heading; defaults by `type` (e.g. Error). */
  title?: string
  /** Called when the dialog is closed for any reason (X, backdrop, Escape, auto timer, Ok). */
  onDismiss?: () => void
}

const NOTIFICATION_MODAL_TITLE_ID = 'liberty-notification-modal-title'

/** Lucide `circle-alert` — stroke uses `currentColor` from `.validationMessageIcon` (red-600). */
const CIRCLE_ALERT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>`

const DEFAULT_NOTIFICATION_TITLE: Record<NotificationType, string> = {
  success: NOTIFICATION_TITLES.success,
  error: NOTIFICATION_TITLES.error,
  warning: NOTIFICATION_TITLES.warning,
  info: NOTIFICATION_TITLES.info,
}

const getToastStyles = (type: NotificationType): string => {
  const base = `
    position: relative;
    padding: 10px 36px 10px 16px;
    border-radius: 9999px;
    max-width: min(420px, calc(100vw - 32px));
    box-sizing: border-box;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    line-height: 1.45;
    word-break: break-word;
  `
  const typeStyles = {
    success: 'background: #ecfdf5; color: #166534; border: 1px solid #bbf7d0;',
    error: 'background: #fef2f2; color: #991b1b; border: 1px solid #fecaca;',
    warning: 'background: #fffbeb; color: #92400e; border: 1px solid #fde68a;',
    info: 'background: #ecfeff; color: #0e7490; border: 1px solid #a5f3fc;',
  }
  return base + typeStyles[type]
}

let openModalCount = 0

function lockBodyScroll() {
  openModalCount += 1
  if (openModalCount === 1) {
    document.body.style.overflow = 'hidden'
  }
}

function unlockBodyScroll() {
  openModalCount = Math.max(0, openModalCount - 1)
  if (openModalCount === 0) {
    document.body.style.overflow = ''
  }
}

/**
 * Compact top-centered toast — no backdrop, does not block the page.
 */
function showToastNotification(
  message: string,
  options: { duration?: number; type?: NotificationType; onDismiss?: () => void } = {}
): void {
  const { duration = 4500, type = 'success', onDismiss } = options

  document.querySelectorAll('[data-toast-notification]').forEach((n) => n.remove())

  const wrap = document.createElement('div')
  wrap.setAttribute('data-toast-notification', 'true')
  wrap.style.cssText = `
    position: fixed;
    top: max(20px, env(safe-area-inset-top, 0px));
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    pointer-events: none;
  `

  if (!document.getElementById('liberty-toast-keyframes')) {
    const style = document.createElement('style')
    style.id = 'liberty-toast-keyframes'
    style.textContent = `
      @keyframes liberty-toast-in {
        from { opacity: 0; transform: translateY(-6px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `
    document.head.appendChild(style)
  }

  const inner = document.createElement('div')
  inner.style.cssText = getToastStyles(type) + ' animation: liberty-toast-in 0.2s ease-out;'
  inner.style.pointerEvents = 'auto'
  inner.setAttribute('role', 'status')
  inner.setAttribute('aria-live', 'polite')
  inner.innerHTML = message

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.innerHTML = '×'
  closeBtn.setAttribute('aria-label', 'Dismiss')
  closeBtn.style.cssText = `
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    background: none;
    border: none;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    color: inherit;
    opacity: 0.65;
    padding: 4px;
  `
  closeBtn.onmouseover = () => {
    closeBtn.style.opacity = '1'
  }
  closeBtn.onmouseout = () => {
    closeBtn.style.opacity = '0.65'
  }

  let dismissed = false
  const dismiss = () => {
    if (dismissed) return
    dismissed = true
    if (wrap.parentNode) wrap.remove()
    onDismiss?.()
  }

  closeBtn.onclick = (e) => {
    e.stopPropagation()
    dismiss()
  }
  inner.appendChild(closeBtn)
  wrap.appendChild(inner)
  document.body.appendChild(wrap)

  if (duration > 0) {
    setTimeout(() => {
      if (wrap.parentNode) dismiss()
    }, duration)
  }
}

/**
 * Centered modal with backdrop — for errors, long HTML, or when `onDismiss` must run after blocking UI (e.g. file preview).
 */
export function showNotification(message: string, options: NotificationOptions = {}): void {
  const { duration = 5000, type = 'success', onDismiss, title } = options
  const modalTitle = title ?? DEFAULT_NOTIFICATION_TITLE[type]

  const existingNotifications = document.querySelectorAll('[data-notification-modal]')
  if (existingNotifications.length > 0) {
    openModalCount = 0
    document.body.style.overflow = ''
    existingNotifications.forEach((n) => n.remove())
  }

  lockBodyScroll()

  const wrapper = document.createElement('div')
  wrapper.setAttribute('data-notification-modal', 'true')
  wrapper.className = modalStyles.modalOverlay
  wrapper.style.zIndex = '10001'
  wrapper.setAttribute('role', 'presentation')

  const content = document.createElement('div')
  content.className = `${modalStyles.modalContent} ${modalStyles.modalContentCompact}`
  content.setAttribute('role', 'alertdialog')
  content.setAttribute('aria-modal', 'true')
  content.setAttribute('aria-labelledby', NOTIFICATION_MODAL_TITLE_ID)
  content.style.pointerEvents = 'auto'
  content.onclick = (e) => e.stopPropagation()

  const validationLike =
    type === 'error' ||
    modalTitle === NOTIFICATION_TITLES.error ||
    modalTitle.toLowerCase().includes('fail') ||
    modalTitle.toLowerCase().includes('error') ||
    modalTitle.toLowerCase().includes('validation')

  const header = document.createElement('div')
  header.className = validationLike ? modalStyles.validationCompactHeader : modalStyles.modalHeader

  const heading = document.createElement('h3')
  heading.id = NOTIFICATION_MODAL_TITLE_ID
  heading.textContent = modalTitle

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = validationLike
    ? `${modalStyles.closeButton} ${modalStyles.validationCompactClose}`
    : modalStyles.closeButton
  closeBtn.setAttribute('aria-label', 'Close')
  closeBtn.innerHTML = '×'
  closeBtn.style.fontSize = '22px'
  closeBtn.style.lineHeight = '1'

  header.appendChild(heading)
  header.appendChild(closeBtn)

  const body = document.createElement('div')
  body.className = validationLike ? modalStyles.validationCompactBody : modalStyles.modalBody
  if (validationLike) {
    const bodyInner = document.createElement('div')
    bodyInner.className = `${modalStyles.rentConfirmMessage} validation-message-html`
    bodyInner.style.textAlign = 'center'
    if (/<[a-z][\s\S]*>/i.test(message.trim())) {
      bodyInner.innerHTML = message
    } else {
      bodyInner.textContent = message
    }
    body.appendChild(bodyInner)
  } else {
    const bodyInner = document.createElement('div')
    bodyInner.className =
      'validation-message-html text-left text-sm leading-relaxed text-[#232946] [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:underline'
    bodyInner.innerHTML = message
    body.appendChild(bodyInner)
  }

  const footer = document.createElement('div')
  footer.className = validationLike
    ? `${modalStyles.modalFooterConfirmTight} ${modalStyles.modalValidationFooterAligned}`
    : `${modalStyles.modalFooter} ${modalStyles.modalFooterCentered}`

  const okBtn = document.createElement('button')
  okBtn.type = 'button'
  okBtn.className = modalStyles.btnSaveChangesPrimary
  okBtn.textContent = 'Ok'

  footer.appendChild(okBtn)

  content.appendChild(header)
  content.appendChild(body)
  content.appendChild(footer)

  let dismissed = false
  const dismiss = () => {
    if (dismissed) return
    dismissed = true
    document.removeEventListener('keydown', onEscape)
    if (wrapper.parentNode) {
      wrapper.remove()
    }
    unlockBodyScroll()
    onDismiss?.()
  }

  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      dismiss()
    }
  }
  document.addEventListener('keydown', onEscape)

  closeBtn.onclick = () => dismiss()
  okBtn.onclick = () => dismiss()
  wrapper.onclick = (e) => {
    if (e.target === wrapper) dismiss()
  }

  wrapper.appendChild(content)
  document.body.appendChild(wrapper)

  if (duration > 0) {
    setTimeout(() => {
      if (wrapper.parentNode) dismiss()
    }, duration)
  }
}

/** Short success feedback — toast only (e.g. “Document file Removed”). */
export function showSuccess(message: string, duration = 4500, onDismiss?: () => void): void {
  showToastNotification(message, { type: 'success', duration, onDismiss })
}

/** Show error — modal so HTML / validation lists stay readable. Optional `title` overrides the default "Error" heading. */
export function showError(message: string, autoClose = true, onDismiss?: () => void, title?: string): void {
  if (
    !message ||
    message === 'GDPR_CONSENT_REQUIRED' ||
    message === 'Service agreement acknowledgement required.'
  ) {
    return
  }
  showNotification(message, {
    type: 'error',
    duration: autoClose ? 8000 : 0,
    onDismiss,
    title,
  })
}

/** Warning — toast. */
export function showWarning(message: string, duration = 5500, onDismiss?: () => void): void {
  showToastNotification(message, { type: 'warning', duration, onDismiss })
}

/** Info — toast. */
export function showInfo(message: string, duration = 4500, onDismiss?: () => void): void {
  showToastNotification(message, { type: 'info', duration, onDismiss })
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Loose shape of the various error objects these helpers defensively probe
 * (raw AxiosError, apiClient ApiError, or a plain object). */
type ErrorLike = {
  response?: { data?: unknown }
  data?: unknown
  error?: unknown
  errors?: unknown
  message?: unknown
}

function pickReferenceId(rawError: unknown): string | number | undefined {
  const error = rawError as ErrorLike
  const candidates = [
    error?.response?.data,
    error?.data,
    typeof error?.error === 'object' ? error.error : null,
  ]
  for (const d of candidates) {
    if (!d || typeof d !== 'object' || Array.isArray(d)) continue
    const o = d as Record<string, unknown>
    const ref =
      o.reference_id ?? o.ref_id ?? o.error_id ?? o.referenceId ?? o.reference
    if (ref !== undefined && ref !== null && String(ref) !== '') return ref as string | number
  }
  return undefined
}

function isAxiosGenericMessage(m: string): boolean {
  return /^Request failed with status code \d+$/i.test(m.trim())
}

/**
 * Red CircleAlert icon (lucide) as inline SVG — matches ValidationModal's text-red-600 alert icon.
 * Prefixes each API-error line in the error popup, replacing the old list bullet.
 */
const ERROR_ROW_ICON =
  "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#dc2626' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' style='flex-shrink:0;margin-top:2px;' aria-hidden='true'><circle cx='12' cy='12' r='10'></circle><line x1='12' x2='12' y1='8' y2='12'></line><line x1='12' x2='12.01' y1='16' y2='16'></line></svg>"

/** Render error lines as red-icon rows (replaces the old disc bullets). Each `inner` is raw HTML. */
function errorListHtml(lines: string[]): string {
  const rows = lines
    .map(
      (inner) =>
        `<li style='display:flex;align-items:flex-start;gap:8px;margin-bottom:4px;text-align:left;'>${ERROR_ROW_ICON}<span>${inner}</span></li>`,
    )
    .join('')
  return `<ul class='error-messages-list' style='list-style:none;padding:0;margin:0;'>${rows}</ul>`
}

function singleErrorListHtml(text: string): string {
  return errorListHtml([escapeHtml(text)])
}

const ERROR_LINE_SKIP_KEYS = new Set([
  'status', 'errors', 'data', 'type', 'email', 'config',
  'headers', 'request', 'response', 'code', 'stack', 'name',
])

/** Flatten an API error body's field values to one line per message (Angular notify(error.error, 0) list). */
function collectFieldErrorLines(body: Record<string, unknown>): string[] {
  const lines: string[] = []
  Object.entries(body).forEach(([key, val]) => {
    if (val === null || val === undefined || val === '' || ERROR_LINE_SKIP_KEYS.has(key)) return
    if (Array.isArray(val)) {
      val.forEach((msg) => {
        if (msg) lines.push(String(msg))
      })
    } else if (typeof val === 'string' || typeof val === 'number') {
      lines.push(String(val))
    }
  })
  return lines
}

export function formatApiError(rawError: unknown): string {
  const error = rawError as ErrorLike
  const rd = error?.response?.data

  /** Resolve a message/error/reason string from a plain API body object. */
  const fromBodyObject = (body: Record<string, unknown> | null): string => {
    if (!body) return ''
    // Angular openaccountninja: prefer fields[*][0] when present
    const fields = body.fields
    if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
      const parts = Object.keys(fields as Record<string, unknown>)
        .map((key) => {
          const val = (fields as Record<string, unknown>)[key]
          if (Array.isArray(val) && val[0]) return String(val[0])
          if (typeof val === 'string' && val.trim()) return val.trim()
          return ''
        })
        .filter(Boolean)
      if (parts.length > 0) return parts.join(' ')
    }
    if (typeof body.error === 'string' && body.error.trim()) return body.error.trim()
    if (typeof body.reason === 'string' && body.reason.trim()) return body.reason.trim()
    if (typeof body.message === 'string' && body.message.trim()) return body.message.trim()
    return ''
  }

  const responseBody =
    rd && typeof rd === 'object' && !Array.isArray(rd) ? (rd as Record<string, unknown>) : null
  const dataBody =
    error?.data && typeof error.data === 'object' && !Array.isArray(error.data)
      ? (error.data as Record<string, unknown>)
      : null
  // Raw body passed directly (Angular error.error / showApiError(data))
  const directBody =
    !responseBody &&
    error &&
    typeof rawError === 'object' &&
    rawError !== null &&
    !Array.isArray(rawError) &&
    !('response' in (rawError as object)) &&
    !('data' in (rawError as object) && 'status' in (rawError as object))
      ? (rawError as Record<string, unknown>)
      : null

  const apiMessageFromString =
    typeof rd === 'string'
      ? rd.trim()
      : typeof rawError === 'string'
        ? rawError.trim()
        : typeof error?.data === 'string'
          ? error.data.trim()
          : ''

  // Angular parity (notify(error.error, 0)): a multi-field validation object
  // ({email:[...], first_name:[...]}) lists every field rather than one summary line.
  const validationBody =
    (error?.error && typeof error.error === 'object' && !Array.isArray(error.error)
      ? (error.error as Record<string, unknown>)
      : null) ||
    responseBody ||
    dataBody ||
    directBody
  if (validationBody && Object.values(validationBody).some((v) => Array.isArray(v))) {
    const lines = collectFieldErrorLines(validationBody)
    if (lines.length > 0) return errorListHtml(lines)
  }

  // Prefer detailed API fields (error/reason) over a short title-like `message`
  // e.g. { message: "Credit Report Failed", error: "<actual reason>" }
  const primary =
    fromBodyObject(responseBody) ||
    fromBodyObject(dataBody) ||
    fromBodyObject(directBody) ||
    apiMessageFromString ||
    (typeof error?.message === 'string' && error.message.trim() && !isAxiosGenericMessage(error.message)
      ? error.message.trim()
      : '')

  if (primary) {
    const ref = pickReferenceId(error)
    let line = primary
    if (ref !== undefined && ref !== null && String(ref) !== '' && !primary.includes(String(ref))) {
      line += ` (Reference: ${escapeHtml(String(ref))})`
    }
    return errorListHtml([line])
  }

  const errorData = error?.error || error?.errors || error?.response?.data || error

  if (typeof errorData === 'string' || errorData instanceof String) {
    return errorListHtml([String(errorData)])
  }

  if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
    const lines = collectFieldErrorLines(errorData as Record<string, unknown>)
    if (lines.length > 0) {
      return errorListHtml(lines)
    }
  }
  const fallbackMessage = error?.message || 'An error occurred. Please try again.'
  return singleErrorListHtml(
    isAxiosGenericMessage(String(fallbackMessage)) ? 'An error occurred. Please try again.' : String(fallbackMessage)
  )
}

/**
 * Server / API failures — same chrome as {@link showValidationMessage} (white dialog, Ok),
 * body from {@link formatApiError} (string / HTML / object shapes, like Angular `notify(error.error, 0)`).
 */
export function showApiError(error: unknown, onDismiss?: () => void): void {
  // HTTP 307 GDPR gate — Angular completes silently; do not show a toast/banner.
  if (isGdprConsentRequiredError(error)) return

  // Canceled / aborted requests (e.g. AbortController search cancellation) — do not show popup
  if (
    axios.isCancel(error) ||
    (error as { name?: string })?.name === 'CanceledError' ||
    (error as { name?: string })?.name === 'AbortError' ||
    (error as { code?: string })?.code === 'ERR_CANCELED' ||
    (typeof (error as { message?: string })?.message === 'string' &&
      (error as { message: string }).message.toLowerCase().includes('canceled'))
  ) {
    return
  }

  const err = error as ErrorLike & { data?: unknown }
  // Prefer the raw API body when the interceptor preserved it (Angular `error.error`).
  const source =
    err?.data !== undefined && err.data !== null
      ? err.data
      : error
  const body = formatApiError(source as Record<string, unknown>)
  if (!body || body === 'GDPR_CONSENT_REQUIRED') return

  if (body.includes(REIMBURSEMENT_POLICY_EXPIRED_MESSAGE)) {
    showValidationMessage(body, 'Policy Expired', onDismiss)
    return
  }

  showValidationMessage(body, 'Message', onDismiss)
}

/**
 * Extract plain message lines from formatApiError HTML (ul/li/span) or plain text.
 * Used by product validation chrome so body matches the filled-icon design.
 */
function extractValidationLines(message: string): string[] {
  const trimmed = message.trim()
  if (!trimmed) return ['Please correct the highlighted fields.']

  if (!/<[a-z][\s\S]*>/i.test(trimmed)) {
    const lines = trimmed
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    return lines.length > 0 ? lines : [trimmed]
  }

  const host = document.createElement('div')
  host.innerHTML = trimmed
  const spanLines = Array.from(host.querySelectorAll('li span'))
    .map((el) => (el.textContent || '').trim())
    .filter(Boolean)
  if (spanLines.length > 0) return spanLines

  const liLines = Array.from(host.querySelectorAll('li'))
    .map((el) => (el.textContent || '').trim())
    .filter(Boolean)
  if (liLines.length > 0) return liLines

  const text = (host.textContent || '').trim()
  return text ? [text] : ['Please correct the highlighted fields.']
}

/**
 * Product-module validation popup (image 2 style):
 * left "Validation" title + header divider, filled red "!" icon rows, Cancel + Continue.
 * Confirmation dialogs (ConfirmDialog) are intentionally unchanged.
 */
export function showProductValidationMessage(
  message: string,
  onDismiss?: () => void
): void {
  const existingNotifications = document.querySelectorAll('[data-validation-modal]')
  if (existingNotifications.length > 0) {
    openModalCount = 0
    document.body.style.overflow = ''
    existingNotifications.forEach((n) => n.remove())
  }

  lockBodyScroll()

  const backdrop = document.createElement('div')
  backdrop.setAttribute('data-validation-modal', 'true')
  backdrop.setAttribute('data-product-validation-modal', 'true')
  backdrop.className = `${modalStyles.modalOverlay}`
  backdrop.style.zIndex = '10001'
  backdrop.setAttribute('role', 'presentation')

  const panel = document.createElement('div')
  panel.className = [
    modalStyles.modalContent,
    modalStyles.modalContentCompact,
    modalStyles.modalContentValidationAligned,
  ].join(' ')
  panel.setAttribute('role', 'alertdialog')
  panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-labelledby', 'product-validation-modal-title')
  panel.style.pointerEvents = 'auto'
  panel.onclick = (e) => e.stopPropagation()

  const header = document.createElement('div')
  header.className = productValidationStyles.productValidationHeader

  const heading = document.createElement('h3')
  heading.id = 'product-validation-modal-title'
  heading.textContent = 'Validation'

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = `${modalStyles.closeButton} ${productValidationStyles.productValidationClose}`
  closeBtn.setAttribute('aria-label', 'Close')
  closeBtn.innerHTML = '×'
  closeBtn.style.fontSize = '22px'
  closeBtn.style.lineHeight = '1'

  header.appendChild(heading)
  header.appendChild(closeBtn)

  const body = document.createElement('div')
  body.className = productValidationStyles.productValidationBody

  const list = document.createElement('div')
  list.className = productValidationStyles.productValidationMessageList

  extractValidationLines(message).forEach((item) => {
    const row = document.createElement('div')
    row.className = productValidationStyles.productValidationIconRow

    const icon = document.createElement('span')
    icon.className = productValidationStyles.productValidationIconMark
    icon.setAttribute('aria-hidden', 'true')
    icon.textContent = '!'

    const text = document.createElement('p')
    text.className = productValidationStyles.productValidationMessageText
    text.textContent = item

    row.appendChild(icon)
    row.appendChild(text)
    list.appendChild(row)
  })

  body.appendChild(list)

  const footer = document.createElement('div')
  footer.className = productValidationStyles.productValidationFooter

  const cancelBtn = document.createElement('button')
  cancelBtn.type = 'button'
  cancelBtn.textContent = 'Cancel'
  cancelBtn.className = `${modalStyles.btnSaveChangesSecondary} ${productValidationStyles.productValidationAction}`

  const continueBtn = document.createElement('button')
  continueBtn.type = 'button'
  continueBtn.textContent = 'Continue'
  continueBtn.className = `${modalStyles.btnSaveChangesPrimary} ${productValidationStyles.productValidationAction}`

  footer.appendChild(cancelBtn)
  footer.appendChild(continueBtn)

  let dismissed = false
  const dismiss = () => {
    if (dismissed) return
    dismissed = true
    document.removeEventListener('keydown', onEscape)
    if (backdrop.parentNode) backdrop.remove()
    unlockBodyScroll()
    onDismiss?.()
  }

  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      dismiss()
    }
  }
  document.addEventListener('keydown', onEscape)

  closeBtn.onclick = () => dismiss()
  cancelBtn.onclick = () => dismiss()
  continueBtn.onclick = () => dismiss()
  backdrop.onclick = (e) => {
    if (e.target === backdrop) dismiss()
  }

  panel.appendChild(header)
  panel.appendChild(body)
  panel.appendChild(footer)
  backdrop.appendChild(panel)
  document.body.appendChild(backdrop)
}

/**
 * Product module — backend / form validation errors using {@link showProductValidationMessage}.
 * Leaves ConfirmDialog / confirmation flows alone.
 */
export function showProductApiError(error: unknown, onDismiss?: () => void): void {
  if (isGdprConsentRequiredError(error)) return

  const err = error as ErrorLike & { data?: unknown }
  const source =
    err?.data !== undefined && err.data !== null
      ? err.data
      : error
  const body = formatApiError(source as Record<string, unknown>)
  if (!body || body === 'GDPR_CONSENT_REQUIRED') return

  if (body.includes(REIMBURSEMENT_POLICY_EXPIRED_MESSAGE)) {
    showValidationMessage(body, 'Policy Expired', onDismiss)
    return
  }

  showProductValidationMessage(body, onDismiss)
}

/**
 * Validation / notify-style modal — same React shell as {@link ValidationModal} (compact card, centered title, blue Ok).
 */
export function showValidationMessage(
  message: string,
  title: string = NOTIFICATION_TITLES.error,
  onDismiss?: () => void
): void {
  const existingNotifications = document.querySelectorAll('[data-validation-modal]')
  if (existingNotifications.length > 0) {
    openModalCount = 0
    document.body.style.overflow = ''
    existingNotifications.forEach((n) => n.remove())
  }

  lockBodyScroll()

  const backdrop = document.createElement('div')
  backdrop.setAttribute('data-validation-modal', 'true')
  backdrop.className = `${modalStyles.modalOverlay}`
  backdrop.style.zIndex = '10001'
  backdrop.setAttribute('role', 'presentation')

  const panel = document.createElement('div')
  panel.className = [
    modalStyles.modalContent,
    modalStyles.modalContentCompact,
    modalStyles.modalContentValidationAligned,
  ].join(' ')
  panel.setAttribute('role', 'alertdialog')
  panel.setAttribute('aria-modal', 'true')
  panel.style.pointerEvents = 'auto'
  panel.onclick = (e) => e.stopPropagation()

  const header = document.createElement('div')
  header.className = modalStyles.validationCompactHeader

  const heading = document.createElement('h3')
  heading.textContent = title

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = `${modalStyles.closeButton} ${modalStyles.validationCompactClose}`
  closeBtn.setAttribute('aria-label', 'Close')
  closeBtn.innerHTML = '×'
  closeBtn.style.fontSize = '22px'
  closeBtn.style.lineHeight = '1'

  header.appendChild(heading)
  header.appendChild(closeBtn)

  const body = document.createElement('div')
  body.className = `${modalStyles.validationCompactBody} ${modalStyles.validationCompactBodyAligned}`

  const isHtml = /<[a-z][\s\S]*>/i.test(message.trim())
  if (isHtml) {
    const msg = document.createElement('div')
    msg.className = `${modalStyles.rentConfirmMessage} validation-message-html`
    msg.innerHTML = message
    body.appendChild(msg)
  } else {
    const lines = message
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    const listItems =
      lines.length > 0 ? lines : message.trim() ? [message.trim()] : ['Please correct the highlighted fields.']

    const wrap = document.createElement('div')
    wrap.className = modalStyles.validationMessageHtmlWrap
    const list = document.createElement('div')
    list.className = modalStyles.validationMessageList

    listItems.forEach((item) => {
      const row = document.createElement('div')
      row.className = modalStyles.validationMessageIconRow

      const iconWrap = document.createElement('div')
      iconWrap.className = modalStyles.validationMessageIcon
      iconWrap.setAttribute('aria-hidden', 'true')
      iconWrap.innerHTML = CIRCLE_ALERT_SVG

      const text = document.createElement('p')
      text.className = 'm-0 leading-relaxed text-[#232946]'
      text.textContent = item

      row.appendChild(iconWrap)
      row.appendChild(text)
      list.appendChild(row)
    })

    wrap.appendChild(list)
    body.appendChild(wrap)
  }

  const footer = document.createElement('div')
  footer.className = `${modalStyles.modalFooterConfirmTight} ${modalStyles.modalValidationFooterAligned}`

  const okBtn = document.createElement('button')
  okBtn.type = 'button'
  okBtn.textContent = 'Ok'
  okBtn.className = modalStyles.btnSaveChangesPrimary

  footer.appendChild(okBtn)

  let dismissed = false
  const dismiss = () => {
    if (dismissed) return
    dismissed = true
    document.removeEventListener('keydown', onEscape)
    if (backdrop.parentNode) backdrop.remove()
    unlockBodyScroll()
    onDismiss?.()
  }

  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      dismiss()
    }
  }
  document.addEventListener('keydown', onEscape)

  closeBtn.onclick = () => dismiss()
  okBtn.onclick = () => dismiss()
  backdrop.onclick = (e) => {
    if (e.target === backdrop) dismiss()
  }

  panel.appendChild(header)
  panel.appendChild(body)
  panel.appendChild(footer)
  backdrop.appendChild(panel)
  document.body.appendChild(backdrop)
}
